import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useOrg } from '../data/orgs';
import { useVisibleDeals } from '../data/permissions';
import { openCommandBar } from '../components/CommandBar';
import { openGuide } from '../components/GuidePanel';
import Icon from '../components/Icons';
import { initialsOf } from '../components/Navbar';
import { OrgLobby } from './Deals';
import './Home.css';

const APP_NAME = process.env.REACT_APP_APP_NAME || 'fast.legal';

// Doc ingest/readiness → semantic badge (status colors, never brand).
const DOC_BADGE = {
  ready: ['Ready', 'b-ok'],
  queued: ['Processing', 'b-info'],
  processing: ['Processing', 'b-info'],
  failed: ['Failed', 'b-danger'],
};

const fmtWhen = (ms) => {
  if (!ms) return '—';
  const d = new Date(ms);
  const now = new Date();
  const sameDay = (a, b) => a.toDateString() === b.toDateString();
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (sameDay(d, now)) return `Today, ${time}`;
  const yday = new Date(now);
  yday.setDate(now.getDate() - 1);
  if (sameDay(d, yday)) return `Yesterday, ${time}`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Recently-opened deal ids (written by Sidebar's recordRecentDeal).
const readRecents = (orgId) => {
  try {
    return JSON.parse(localStorage.getItem(`fl-recent-deals:${orgId}`) || '[]');
  } catch {
    return [];
  }
};

const ASK_CHIPS = [
  'How do I draft an NDA?',
  'How do I upload a counterparty draft?',
  'What is the difference between clean and redline views?',
  'How do deal points and redlines work together?',
];

const AI_ACTIONS = [
  { icon: 'list', title: 'Summarize', sub: 'Get a plain-English summary', verb: 'ask',
    text: 'Summarize the latest version of the working draft — what changed and why it matters' },
  { icon: 'shield', title: 'Identify risks', sub: 'Find key risks and issues', verb: 'ask',
    text: 'Identify the key risks and open points in the current draft' },
  { icon: 'pen', title: 'Suggest edits', sub: 'Improve language and structure', verb: 'do',
    text: 'Tighten the drafting in the working document and file a reviewable edit' },
  { icon: 'compare', title: 'Compare versions', sub: 'See what changed', verb: 'ask',
    text: 'Compare the two most recent versions — what moved, and who moved it?' },
  { icon: 'chat', title: 'Answer questions', sub: 'Ask about any document', verb: 'ask', text: '' },
];

export default function Home() {
  const { user } = useAuth();
  const { org, orgId, memberships, role } = useOrg();
  const { deals } = useVisibleDeals();
  const navigate = useNavigate();
  const [ask, setAsk] = useState('');
  const [recentDocs, setRecentDocs] = useState(null); // [{ dealId, dealName, ...doc }]

  const isAdmin = ['admin', 'owner'].includes(role);

  // Deals ordered by recently-opened, then creation date — drives the
  // "Review a Document" card, quick links, and the recent-docs fetch.
  const ordered = useMemo(() => {
    const rank = new Map(readRecents(orgId).map((id, i) => [id, i]));
    return [...(deals || [])].sort((a, b) => {
      const ra = rank.has(a.id) ? rank.get(a.id) : Infinity;
      const rb = rank.has(b.id) ? rank.get(b.id) : Infinity;
      if (ra !== rb) return ra - rb;
      return (b.createdAt || 0) - (a.createdAt || 0);
    });
  }, [deals, orgId]);

  // Recent documents across the most relevant projects (subcollection reads,
  // same pattern as the Projects search index — no collection-group rules).
  useEffect(() => {
    if (!orgId || deals === null) return undefined;
    if (!deals.length) { setRecentDocs([]); return undefined; }
    let alive = true;
    (async () => {
      try {
        const { db } = await import('../firebaseConfig');
        const targets = ordered.slice(0, 6);
        const lists = await Promise.all(
          targets.map(async (d) => {
            const snap = await db.collection(`orgs/${orgId}/deals/${d.id}/docs`).get();
            return snap.docs.map((x) => ({ id: x.id, dealId: d.id, dealName: d.name, ...x.data() }));
          })
        );
        if (!alive) return;
        const merged = lists
          .flat()
          .sort((a, b) => (b.updatedAt || b.createdAt || 0) - (a.updatedAt || a.createdAt || 0))
          .slice(0, 8);
        setRecentDocs(merged);
      } catch {
        if (alive) setRecentDocs([]);
      }
    })();
    return () => { alive = false; };
  }, [orgId, deals, ordered]);

  if (memberships === null) {
    return (
      <div className="cs-page">
        <div className="cs-skeleton" style={{ height: 120 }} />
      </div>
    );
  }
  if (!memberships.length) return <OrgLobby />;

  const recentDeal = ordered[0] || null;
  const recentDoc = (recentDocs || [])[0] || null;
  const firstName = (user?.displayName || user?.email || '').split(/[\s@]/)[0];

  const submitAsk = (e) => {
    e?.preventDefault();
    openCommandBar({ verb: 'ask', instruction: ask.trim() });
    setAsk('');
  };

  return (
    <div className="cs-page u-stack m-rise fl-home" style={{ '--gap': 'var(--sp-6)' }}>
      <header className="fl-home-hero">
        <h1>Your Legal Work, <span className="fl-accent">Accelerated.</span></h1>
        <p>
          {firstName ? `Welcome back, ${firstName} — c` : 'C'}reate, review, edit, and manage
          your most important documents with AI.
        </p>
      </header>

      <div className="fl-home-grid">
        {/* ================= Main column ================= */}
        <div className="u-stack" style={{ '--gap': 'var(--sp-6)' }}>
          <div className="fl-actions">
            <button type="button" className="fl-action" onClick={() => navigate('/projects?new=1')}>
              <span className="fl-action-icon"><Icon name="doc" size={20} /></span>
              <strong>Create a Document</strong>
              <span>Start a project, then draft from a description or a template</span>
              <span className="fl-action-go"><Icon name="arrowRight" size={16} /></span>
            </button>
            <button
              type="button"
              className="fl-action blue"
              onClick={() => navigate(recentDeal ? `/deals/${recentDeal.id}` : '/projects')}
            >
              <span className="fl-action-icon"><Icon name="search" size={20} /></span>
              <strong>Review a Document</strong>
              <span>Upload a draft and get AI analysis, risks and suggestions</span>
              <span className="fl-action-go"><Icon name="arrowRight" size={16} /></span>
            </button>
            <button
              type="button"
              className="fl-action purple"
              onClick={() =>
                navigate(recentDoc ? `/deals/${recentDoc.dealId}/docs/${recentDoc.id}` : '/projects')}
            >
              <span className="fl-action-icon"><Icon name="pen" size={20} /></span>
              <strong>Edit or Rewrite</strong>
              <span>Make changes with AI assistance — every edit reviewable</span>
              <span className="fl-action-go"><Icon name="arrowRight" size={16} /></span>
            </button>
            {isAdmin ? (
              <button type="button" className="fl-action green" onClick={() => navigate('/team')}>
                <span className="fl-action-icon"><Icon name="users" size={20} /></span>
                <strong>Manage Access</strong>
                <span>Control who can view, edit, and approve</span>
                <span className="fl-action-go"><Icon name="arrowRight" size={16} /></span>
              </button>
            ) : (
              <button type="button" className="fl-action green" onClick={() => navigate('/projects')}>
                <span className="fl-action-icon"><Icon name="folder" size={20} /></span>
                <strong>Browse Projects</strong>
                <span>Everything you can see, search, and open</span>
                <span className="fl-action-go"><Icon name="arrowRight" size={16} /></span>
              </button>
            )}
          </div>

          {/* Ask box — opens the ⌘K palette prefilled. */}
          <div className="cs-card fl-ask">
            <div className="fl-ask-head">
              <Icon name="sparkle" size={18} /> Ask {APP_NAME}
            </div>
            <form className="fl-ask-row" onSubmit={submitAsk}>
              <input
                className="cs-input"
                value={ask}
                onChange={(e) => setAsk(e.target.value)}
                placeholder={'Describe what you need — e.g. "Draft an employment agreement for a VP of Sales"'}
                aria-label={`Ask ${APP_NAME}`}
              />
              <button className="cs-btn cs-btn-primary" type="submit">
                <Icon name="arrowRight" size={16} />
              </button>
            </form>
            <div className="fl-ask-chips">
              {ASK_CHIPS.map((c) => (
                <button
                  key={c}
                  type="button"
                  className="cs-chip"
                  style={{ cursor: 'pointer' }}
                  onClick={() => openCommandBar({ verb: 'ask', instruction: c })}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          {/* Recent documents across projects. */}
          <section className="fl-recent u-stack" style={{ '--gap': 'var(--sp-3)' }}>
            <div className="u-split">
              <h2 style={{ margin: 0, fontSize: 'var(--fs-lg)' }}>Recent Documents</h2>
              <Link to="/projects" className="u-small" style={{ color: 'var(--surf)' }}>
                View all →
              </Link>
            </div>
            {recentDocs === null && <div className="cs-skeleton" style={{ height: 160 }} />}
            {recentDocs && !recentDocs.length && (
              <div className="cs-empty">
                No documents yet — create a project, then upload a draft or let the drafting
                agent author one. Press <kbd style={{ padding: '0 4px' }}>⌘K</kbd> anytime to ask how.
              </div>
            )}
            {recentDocs && recentDocs.length > 0 && (
              <div className="cs-card" style={{ overflow: 'auto' }}>
                <table className="cs-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Project</th>
                      <th>Last Modified</th>
                      <th>Status</th>
                      <th>Owner</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentDocs.map((doc) => {
                      const [label, tone] = DOC_BADGE[doc.status] || ['Draft', ''];
                      const owner = doc.createdByName || '';
                      return (
                        <tr key={`${doc.dealId}:${doc.id}`}>
                          <td>
                            <Link to={`/deals/${doc.dealId}/docs/${doc.id}`} className="fl-doc-title">
                              <Icon name="doc" size={16} />
                              <span>{doc.title || 'Untitled'}</span>
                            </Link>
                          </td>
                          <td className="u-muted">
                            <Link to={`/deals/${doc.dealId}`} style={{ color: 'inherit' }}>
                              {doc.dealName}
                            </Link>
                          </td>
                          <td className="u-muted u-tabular">{fmtWhen(doc.updatedAt || doc.createdAt)}</td>
                          <td><span className={`cs-badge ${tone}`}>{label}</span></td>
                          <td>
                            {owner ? (
                              <span className="fl-owner">
                                <span className="cs-avatar">{initialsOf(owner)}</span>
                                <span className="u-small u-muted">{owner}</span>
                              </span>
                            ) : (
                              <span className="u-small u-muted">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>

        {/* ================= Right rail ================= */}
        <aside className="fl-rail" aria-label={`${APP_NAME} AI and quick links`}>
          <div className="cs-card fl-rail-card">
            <div className="fl-rail-head"><Icon name="sparkle" size={18} /> {APP_NAME} AI</div>
            <div className="fl-rail-sub">Your legal copilot</div>
            {AI_ACTIONS.map((a) => (
              <button
                key={a.title}
                type="button"
                className="fl-rail-item"
                onClick={() => openCommandBar({ verb: a.verb, instruction: a.text })}
              >
                <Icon name={a.icon} size={17} />
                <span>
                  <strong>{a.title}</strong>
                  <span>{a.sub}</span>
                </span>
              </button>
            ))}
          </div>

          <div className="cs-card fl-rail-card">
            <div className="fl-rail-head">Quick Links</div>
            <button
              type="button"
              className="fl-rail-item"
              onClick={() => navigate(recentDeal ? `/deals/${recentDeal.id}` : '/projects')}
            >
              <Icon name="upload" size={17} />
              <span><strong>Upload a document</strong></span>
            </button>
            <button type="button" className="fl-rail-item" onClick={() => navigate('/projects?new=1')}>
              <Icon name="plus" size={17} />
              <span><strong>Start a project</strong></span>
            </button>
            {isAdmin && (
              <button type="button" className="fl-rail-item" onClick={() => navigate('/team')}>
                <Icon name="users" size={17} />
                <span><strong>Invite a team member</strong></span>
              </button>
            )}
            <button type="button" className="fl-rail-item" onClick={() => openGuide()}>
              <Icon name="book" size={17} />
              <span><strong>Open the platform guide</strong></span>
            </button>
          </div>

          <div className="fl-promo">
            <strong>Better Agreements.<br />Build Bigger Futures.</strong>
            <span className="fl-promo-brand">{APP_NAME}</span>
            <span>Legal intelligence for what's next</span>
          </div>
        </aside>
      </div>

      <footer className="fl-home-foot">
        <span>Confidential &amp; Proprietary · © {new Date().getFullYear()} {APP_NAME}. All rights reserved.</span>
        <span>{org?.name || ''}</span>
      </footer>
    </div>
  );
}
